import { Box, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EngagementStatusBarChartComponent } from './engagement-status-by-language-count-bar-chart.component';
import { EngagementStatusByLanguageDto, ProviderChartTypeArray, ProviderChartTypeEnum } from '@families-link/shared';
import { useGetEngagementStatusByLanguageQuery } from '../../../../store/api/analytics.api';
import dayjs from 'dayjs';
import { FilterValuesStrings } from '../../pages/analytics.page';
import { FadeSpinner } from '../../../_core/components/_ui/fade-spinner';
import { AnalyticsFilters } from '../../../_core/interfaces/analytics-filters.interface';

export type EngagementStatusByLanguageChartProps = {
	transformedFilterValues: FilterValuesStrings;
	currentDistrictId?: string | null;
	authToken?: string;
};

export type EngagementStatusByLanguageChartRow = {
	language: string;
	Completed: number;
	'In Progress': number;
	New: number;
	Other: number;
};
function toPercentFloat(value: number, total: number) {
	if (total === 0) return 0;
	return (value / total) * 100;
}

function mapToPercentChartRows(apiData: EngagementStatusByLanguageDto[]): EngagementStatusByLanguageChartRow[] {
	return apiData.map((row) => {
		const counts = {
			Completed: row.completed || 0,
			'In Progress': row.inProgress || 0,
			New: row.new || 0,
			Other: row.others || 0,
		};

		const sumOfComponents = Object.values(counts).reduce((acc, v) => acc + v, 0);
		const effectiveTotal = row.total && row.total >= sumOfComponents ? row.total : sumOfComponents || 0;

		return {
			language: row.language,
			Completed: toPercentFloat(counts.Completed, effectiveTotal),
			'In Progress': toPercentFloat(counts['In Progress'], effectiveTotal),
			New: toPercentFloat(counts.New, effectiveTotal),
			Other: toPercentFloat(counts.Other, effectiveTotal),
		};
	});
}

export const EngagementStatusByLanguageChart: FC<EngagementStatusByLanguageChartProps> = (props) => {
	const { currentDistrictId, transformedFilterValues, authToken } = props;
	const [t] = useTranslation();

	const normalArgs: AnalyticsFilters = {
		start:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[0]
				? dayjs(transformedFilterValues.duration[0]).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		end:
			transformedFilterValues?.duration?.length && transformedFilterValues.duration[1]
				? dayjs(transformedFilterValues.duration[1]).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ')
				: '',
		schools: transformedFilterValues?.schoolIds,
		district: currentDistrictId,
		districts: transformedFilterValues?.districtIds,
		services: transformedFilterValues?.serviceIds,
		programs: transformedFilterValues?.programIds,
		providers: transformedFilterValues?.providerIds,
		languages: transformedFilterValues?.languages,
		engagementStatuses: transformedFilterValues?.engagementStatuses,
		engagementTypes: transformedFilterValues?.engagementTypes,
	};

	const authenticatedNormalArgs = useMemo(
		() => ({
			queryParams: normalArgs,
			authToken,
		}),
		[normalArgs, authToken]
	);

	const providersQuery = useGetEngagementStatusByLanguageQuery(authenticatedNormalArgs, {
		refetchOnMountOrArgChange: true,
	});

	const data = providersQuery.data;
	const isFetching = providersQuery.isFetching;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width: '100%',
				height: '100%',
				gap: 2,
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
				<Typography sx={{ fontSize: '16px', fontWeight: 500, color: '#797F92' }}>
					Engagements Status By Language
				</Typography>
			</Box>
			{isFetching ? (
				<Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
					<FadeSpinner size={51} height={15} width={6} borderRadius={3} />
				</Box>
			) : (
				<Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{data && data.length > 0 ? (
						<EngagementStatusBarChartComponent data={mapToPercentChartRows(data)} />
					) : (
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Typography sx={{ color: (theme) => theme.colors.grayLightBlue, fontSize: '16px', fontWeight: 500 }}>
								No data found for the chart
							</Typography>
						</Box>
					)}
				</Box>
			)}
		</Box>
	);
};
